import type { StoreDashboardQuery } from '@gozoki/api-types'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  fetchStoreDashboardActiveUsers,
  fetchStoreDashboardOverview,
  fetchStoreDashboardTopCategories,
  fetchStoreDashboardTopProducts,
  type StoreDashboardSection,
} from '../queries/store-dashboard.queries'

const STALE_TIME = 60 * 60 * 1000 // aligné sur le TTL backend (1h)

export const storeDashboardSectionKey = (
  section: StoreDashboardSection,
  query: StoreDashboardQuery | null,
) =>
  [
    `store-dashboard-${section}`,
    query === null ? null : [...query.storeIds].sort((l, r) => l - r),
    query?.startDate,
    query?.endDate,
    query?.compStartDate,
    query?.compEndDate,
  ] as const

const useSection = <T>(
  section: StoreDashboardSection,
  query: StoreDashboardQuery | null,
  fetcher: (query: StoreDashboardQuery, signal?: AbortSignal) => Promise<T>,
) =>
  useQuery({
    queryKey: storeDashboardSectionKey(section, query),
    queryFn: ({ signal }) => fetcher(query as StoreDashboardQuery, signal),
    enabled: query !== null && query.storeIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
    retry: false,
  })

export const useStoreDashboardOverview = (query: StoreDashboardQuery | null) =>
  useSection('overview', query, fetchStoreDashboardOverview)
export const useStoreDashboardActiveUsers = (query: StoreDashboardQuery | null) =>
  useSection('app-active-users', query, fetchStoreDashboardActiveUsers)
export const useStoreDashboardTopProducts = (query: StoreDashboardQuery | null) =>
  useSection('top-products', query, fetchStoreDashboardTopProducts)
export const useStoreDashboardTopCategories = (query: StoreDashboardQuery | null) =>
  useSection('top-categories', query, fetchStoreDashboardTopCategories)
